import type { DEGREE } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import cloneDeep from "lodash/cloneDeep";
import filter from "lodash/filter";
import intersection from "lodash/intersection";
import type { Vector3 } from "three";
import * as THREE from "three";
import { Alignment } from "../enum/Alignment";
import { ColorMode } from "../enum/ColorMode";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { RectType } from "../enum/RectType";
import type { CutProperties } from "../interfaces/CutProperties";
import type { Position } from "../interfaces/Position";
import type { RectProperties } from "../interfaces/RectProperties";
import { getOpposingSide } from "../user_options/Side";
import { EdgeInfos } from "./EdgeInfos";
import { EndpointInfos } from "./EndpointInfos";
import { RectPropertiesBySide } from "./RectProperties";

type GroupColorPair = {
  line: THREE.LineBasicMaterial;
  mesh: THREE.MeshBasicMaterial;
};

function rectPropertiesForSide(side: Sides): RectProperties {
  const props = RectPropertiesBySide[side];
  if (props === undefined) {
    throw new Error(`RectPropertiesBySide: missing side ${String(side)}`);
  }
  return props;
}

function edgeAt(edges: EdgeInfos[], idx: EdgeIndex): EdgeInfos {
  const edge = edges[idx];
  if (!edge) {
    throw new Error(`Rect: missing edge at index ${idx}`);
  }
  return edge;
}

function cornerAt(corners: EndpointInfos[], idx: number): EndpointInfos {
  const c = corners[idx];
  if (!c) {
    throw new Error(`Rect: missing corner at index ${idx}`);
  }
  return c;
}

// the default group name is used for sorting in PDFPlan
// on change PDFPLAN has to be adjusted
export const DefaultGroupName = "Korpus";

const availColors = ["red", "green", "blue", "orange", "black", "purple"].map(
  (color) => {
    return {
      line: new THREE.LineBasicMaterial({ color }),
      mesh: new THREE.MeshBasicMaterial({ color }),
    };
  },
); // .reverse();
const groupColors: Record<string, GroupColorPair> = {};

const sphereGeo = new THREE.SphereGeometry(4);

function getLineColorForMode(
  mode: ColorMode,
  edgeTypeColor: GroupColorPair,
  edgeIndex: number,
  groupColor: GroupColorPair | undefined,
): GroupColorPair | undefined {
  if (mode === ColorMode.EDGETYPE) {
    return edgeTypeColor;
  }
  if (mode === ColorMode.EDGEINDEX) {
    return availColors[edgeIndex];
  }
  return groupColor;
}

// gets angular rotation on side in degree, bottom left corner is 0, top left 90
export const getOrientationOnSide = (
  referenceSide: Sides,
  sides: Sides[],
): DEGREE => {
  const props = rectPropertiesForSide(referenceSide);
  const { left, top } = props;
  const isLeft: boolean = sides.includes(left);
  const isTop: boolean = sides.includes(top);
  if (isLeft && isTop) {
    return 90;
  }
  if (!isLeft && isTop) {
    return 180;
  }
  if (!isLeft && !isTop) {
    return 270;
  }
  return 0;
};

// project a 3d point onto one of the sides
// export const projectOntoSide = (point: THREE.Vector3, side: Sides): [number, number] => {
//     return [0, 0];
// };

export class Rect {
  /**
   *
   */
  public group: string = DefaultGroupName;
  /**
   * The material of this part. See Materials.ts for more info.
   */
  public strength: number;
  public addables: unknown[] = [];
  public side: Sides;
  public cutReference: Sides | null = null;
  public corners: EndpointInfos[] = [];
  public edges: EdgeInfos[];
  public type: RectType;
  public properties: RectProperties;

  constructor(side: Sides, size: THREE.Vector3, strength: number) {
    // creates corners
    this.strength = strength;
    this.side = side;
    this.type = RectType.COVERSIDE;
    this.properties = rectPropertiesForSide(side);

    for (const n of [0, 1, 2, 3]) {
      const { verticalDir, horizontalDir, normalDir } = this.properties;
      const outerSize = size
        .clone()
        .add(new THREE.Vector3(strength * 2, strength * 2, strength * 2));
      const primLower = n === 1 || n === 2;
      const secLower = n < 2;
      const innerPos = new THREE.Vector3()
        .add(
          verticalDir
            .clone()
            .multiply(size)
            .multiplyScalar(primLower ? -0.5 : 0.5),
        )
        .add(
          horizontalDir
            .clone()
            .multiply(size)
            .multiplyScalar(secLower ? -0.5 : 0.5),
        )
        .add(normalDir.clone().multiply(size).multiplyScalar(0.5));
      const outerPos = new THREE.Vector3()
        .add(
          verticalDir
            .clone()
            .multiply(outerSize)
            .multiplyScalar(primLower ? -0.5 : 0.5),
        )
        .add(
          horizontalDir
            .clone()
            .multiply(outerSize)
            .multiplyScalar(secLower ? -0.5 : 0.5),
        )
        .add(normalDir.clone().multiply(outerSize).multiplyScalar(0.5));
      this.corners.push(
        new EndpointInfos({
          group: DefaultGroupName,
          isOriginal: true,
          pos: innerPos,
          outerPos,
          sides: [
            innerPos.x > 0 ? Sides.RIGHT : Sides.LEFT,
            innerPos.z < 0 ? Sides.BACK : Sides.FRONT,
            innerPos.y > 0 ? Sides.TOP : Sides.BOTTOM,
          ],
        }),
      );
    }

    // create edges
    this.edges = [];
    for (let n = 0; n < 4; n++) {
      const c0 = this.corners[n];
      const c1 = this.corners[(n + 1) % 4];
      const cStart = this.corners[(n + 0) % 4];
      if (!c0 || !c1 || !cStart) {
        throw new Error("Rect: invariant failed (corners not initialized)");
      }
      const sharedSides = intersection(c0.sides, c1.sides);
      this.edges.push(
        new EdgeInfos({
          end: c1,
          group: DefaultGroupName,
          index: n,
          isOriginal: true,
          sides: sharedSides,
          cutReference: this.side,
          start: cStart,
        }),
      );
    }
  }
  public get id(): string {
    return `${this.group}/${Sides[this.side]}`;
  }

  // get direction(): Direction {
  //     return result!;
  // }
  get center(): Vector3 {
    const result = new THREE.Vector3();
    const c0 = cornerAt(this.corners, 0);
    const c2 = cornerAt(this.corners, 2);
    result.addVectors(c0.innerPos, c2.innerPos);
    result[this.properties.normal] =
      c0.innerPos[this.properties.normal] + c0.outerPos[this.properties.normal];
    result.multiplyScalar(0.5);
    return result;
  }

  get innerCenter(): THREE.Vector3 {
    const result = new THREE.Vector3();
    result.addVectors(
      cornerAt(this.corners, 0).innerPos,
      cornerAt(this.corners, 2).innerPos,
    );
    result.multiplyScalar(0.5);
    return result;
  }

  get outerCenter(): THREE.Vector3 {
    const result = new THREE.Vector3();
    result.addVectors(
      cornerAt(this.corners, 0).outerPos,
      cornerAt(this.corners, 2).outerPos,
    );
    result.multiplyScalar(0.5);
    return result;
  }

  /**
   * outer size
   * @returns {number[]}
   */
  get outerSize(): THREE.Vector2 {
    return new THREE.Vector2(
      edgeAt(this.edges, EdgeIndex.TOP).outerLength,
      edgeAt(this.edges, EdgeIndex.RIGHT).outerLength,
    );
  }

  /**
   * inner size
   * @returns {number[]}
   */
  get innerSize(): THREE.Vector2 {
    return new THREE.Vector2(
      edgeAt(this.edges, EdgeIndex.TOP).innerLength,
      edgeAt(this.edges, EdgeIndex.RIGHT).innerLength,
    );
  }

  get innerArea(): number {
    return (
      edgeAt(this.edges, EdgeIndex.TOP).innerLength *
      edgeAt(this.edges, EdgeIndex.RIGHT).innerLength
    );
  }

  /**
   * panel position
   * @returns {number[]}
   */
  get position2d(): number[] {
    let x = -edgeAt(this.edges, 1).innerLength / 2;
    let y = -edgeAt(this.edges, 0).innerLength / 2;
    switch (this.side) {
      case Sides.FRONT:
        break;
    }

    if ([Sides.LEFT, Sides.RIGHT].includes(this.side)) {
      if (
        edgeAt(this.edges, 2 - (this.side % 2) * 2).type === EdgeType.FROMCUT
      ) {
        x += this.strength;
      }
    } else if (
      edgeAt(this.edges, (this.side % 2) * 2).type === EdgeType.FROMCUT
    ) {
      x += this.strength;
    }
    if (
      edgeAt(this.edges, this.side === Sides.TOP ? 1 : 3).type ===
      EdgeType.FROMCUT
    ) {
      y += this.strength;
    }
    return [x, y];
  }

  public resolvePosition1d(
    position: Position,
    orientationOnSide: OrientationOnRect,
    padding = 0,
    overFullcase = true,
  ): number | null {
    // coordinate to work on
    const coord =
      orientationOnSide === OrientationOnRect.VERTICAL
        ? this.properties.vertical
        : this.properties.horizontal;
    // vector pointing in the direction of the end position for the relevant side
    const dir =
      orientationOnSide === OrientationOnRect.VERTICAL
        ? this.properties.verticalDir
        : this.properties.horizontalDir;
    // calculate coordinate in worldspace
    let p = 0;
    const edgeIndex =
      orientationOnSide === OrientationOnRect.VERTICAL
        ? EdgeIndex.LEFT
        : EdgeIndex.BOTTOM;
    const edge = edgeAt(this.edges, edgeIndex);
    const edgeLength = overFullcase
      ? edge.originalEdgeLength
      : edge.outerLength;
    const offset =
      (edgeLength - position.margin * 2 - 2 * padding) * position.n;
    const points = overFullcase
      ? [edge.start.originalOuterPos[coord], edge.end.originalOuterPos[coord]]
      : [edge.start.outerPos[coord], edge.end.outerPos[coord]];
    const ordered =
      edge.direction.dot(dir) > 0 ? points : points.slice().reverse();
    const startPos = ordered[0];
    const endPos = ordered[1];
    if (startPos === undefined || endPos === undefined) {
      throw new Error("Rect.resolvePosition1d: missing edge endpoints");
    }
    switch (position.alignment) {
      case Alignment.END:
      case Alignment.START: {
        p = startPos + (position.margin + offset + padding) * dir[coord];
        break;
      }
      case Alignment.CENTER: {
        p = (startPos + endPos) / 2 + position.margin * dir[coord];
        break;
      }
    }
    if (
      (edge.start.outerPos[coord] <= p && p <= edge.end.outerPos[coord]) ||
      (edge.start.outerPos[coord] >= p && p >= edge.end.outerPos[coord])
    ) {
      return p;
    } else {
      return null;
    }
  }

  public getNeighbour(side: EdgeIndex): Rect | undefined {
    return edgeAt(this.edges, side).rects.find((r) => r.id !== this.id);
  }

  public getInwards(side: EdgeIndex): THREE.Vector2 {
    switch (side) {
      case EdgeIndex.LEFT:
        return new THREE.Vector2(1, 0);
      case EdgeIndex.RIGHT:
        return new THREE.Vector2(-1, 0);
      case EdgeIndex.BOTTOM:
        return new THREE.Vector2(0, -1);
      case EdgeIndex.TOP:
        return new THREE.Vector2(0, 1);
    }
  }
  public log() {
    console.log(Sides[this.side], this);
  }

  /**
   *
   */
  public getVisual(mode: ColorMode = ColorMode.EDGETYPE): THREE.Object3D {
    const result = new THREE.Object3D();
    this.ensureGroupColor();
    this.addCornerVisuals(result, mode);
    this.addEdgeVisuals(result, mode, "innerPos");
    this.addEdgeVisuals(result, mode, "outerPos");
    return result;
  }

  private ensureGroupColor(): void {
    if (Reflect.has(groupColors, this.group)) {
      return;
    }

    const nextColor = availColors[Object.keys(groupColors).length];
    if (!nextColor) {
      throw new Error("Rect.getVisual: ran out of group colors");
    }
    groupColors[this.group] = nextColor;
  }

  private addCornerVisuals(result: THREE.Object3D, mode: ColorMode): void {
    for (const corner of this.corners) {
      const edgeColorPair = availColors[corner.type] ?? availColors[0];
      if (!edgeColorPair) {
        throw new Error("Rect.getVisual: availColors empty");
      }
      const colorByMode: GroupColorPair | undefined =
        mode === ColorMode.EDGETYPE ? edgeColorPair : groupColors[this.group];
      if (colorByMode === undefined) {
        throw new Error("Rect.getVisual: missing group color for mode");
      }
      const material = colorByMode.mesh;
      const vis = new THREE.Object3D();
      vis.add(new THREE.Mesh(sphereGeo, material));
      vis.position.copy(corner.innerPos);
      vis.userData.comment = "sphere";
      result.add(vis);
      const vis2 = new THREE.Object3D();
      vis2.add(new THREE.Mesh(sphereGeo, material));
      // vis.add(new TextSprite(corner.sides.map((d) => "LRBFDT"[d]).join("")));
      vis2.position.copy(corner.outerPos);
      result.add(vis2);
    }
  }

  private addEdgeVisuals(
    result: THREE.Object3D,
    mode: ColorMode,
    positionKey: "innerPos" | "outerPos",
  ): void {
    for (let n = 0; n < 4; n++) {
      const edge = edgeAt(this.edges, n);
      const edgeTypeColor = availColors[edge.type] ?? availColors[0];
      if (!edgeTypeColor) {
        throw new Error("Rect.getVisual: availColors empty");
      }
      const colorByMode = getLineColorForMode(
        mode,
        edgeTypeColor,
        n,
        groupColors[this.group],
      );
      if (colorByMode === undefined) {
        throw new Error("Rect.getVisual: missing color for edge mode");
      }
      const material = colorByMode.line;
      const geometry = new THREE.BufferGeometry();
      const vertices = new Float32Array([
        edge.start[positionKey].x,
        edge.start[positionKey].y,
        edge.start[positionKey].z,
        edge.end[positionKey].x,
        edge.end[positionKey].y,
        edge.end[positionKey].z,
      ]);
      geometry.setAttribute("position", new THREE.BufferAttribute(vertices, 3));
      const line = new THREE.Line(geometry, material);
      result.add(line);
    }
  }

  private rectTouchesOptionalCutFromSide(cut: CutProperties): boolean {
    const cutFromSide = cut.cutFrom;
    if (cutFromSide === undefined) {
      return true;
    }
    const edgesTouchingCutFrom = filter(this.edges, (edge) =>
      edge.sides.includes(cutFromSide),
    );
    return edgesTouchingCutFrom.length > 0;
  }

  private isParallelToCutReference(
    opposingSide: Sides,
    cut: CutProperties,
  ): boolean {
    return cut.reference === this.side || opposingSide === this.side;
  }

  /**
   * Rect parallel to reference side is not geometrically split; may still join cover group.
   */
  private applyCoverplateWhenParallelToCut(cut: CutProperties): void {
    const onReferenceSide = cut.reference === this.side;
    const assignCoverGroup = onReferenceSide
      ? !cut.invertCover
      : Boolean(cut.invertCover);
    if (assignCoverGroup) {
      this.group = cut.groupName;
    }
    this.type = RectType.COVERPLATE;
    this.cutReference = cut.reference;
  }

  private moveEdgesForSplit(
    secondPart: Rect,
    cut: CutProperties,
    opposingSide: Sides,
    profileExtra: number,
  ): void {
    for (let e = 0; e < 4; e++) {
      const edge = edgeAt(this.edges, e);
      if (!edge.sides.includes(cut.reference)) {
        continue;
      }
      this.cutReference = cut.reference;
      secondPart.cutReference = cut.reference;
      secondPart.type = RectType.COVERSIDE;

      if (cut.invertCover) {
        this.setGroupName(cut.groupName);
      } else {
        secondPart.setGroupName(cut.groupName);
      }
      const { normalDir, normal } = rectPropertiesForSide(cut.reference);
      const newCoord: number =
        edge.start.innerPos[normal] +
        -normalDir[normal] * (cut.offset + profileExtra / 2);
      edge.move(newCoord, cut.reference, cut.groupName);
      edge.cutName = cut.groupName;
      edge.inverted = cut.invertCover === true;
      edge.cut = cut;
      const counterEdge = edgeAt(secondPart.edges, (e + 2) % 4);
      counterEdge.cut = cut;
      counterEdge.cutName = cut.groupName;
      counterEdge.move(newCoord, opposingSide, cut.groupName);
      counterEdge.inverted = cut.invertCover !== true;
    }
  }

  /**
   * executes a cut on itself and returns the cut off piece as new rect
   * @param  {number} {offset how much you move the knife from
   * @param  {} reference
   * @param  {} cutFrom=null if specified: the side where you put the "knife"
   * @param  {Cut2} name}
   * @param  {} noDupli=false
   * @returns Rect
   */
  public applyCut(cut: CutProperties, profileExtra: number): Rect | null {
    const opposingSide = getOpposingSide(cut.reference);

    if (!this.rectTouchesOptionalCutFromSide(cut)) {
      return null;
    }

    if (cut.baseOnly && this.group !== DefaultGroupName) {
      return null;
    }

    /**
     * if the rect is parallel to the reference side it will never be cut
     * it might however be part of another group after the cut
     */
    if (this.isParallelToCutReference(opposingSide, cut)) {
      this.applyCoverplateWhenParallelToCut(cut);
      return null;
    }

    /**
     * if no edge touches the reference side we skip it
     * filter out all edges that lay on the reference side
     */
    const edgesTouchingReference = filter(this.edges, (edge) =>
      edge.sides.includes(cut.reference),
    );
    if (edgesTouchingReference.length === 0) {
      return null;
    }

    const secondPart: Rect = cloneDeep(this);
    this.moveEdgesForSplit(secondPart, cut, opposingSide, profileExtra);
    return secondPart;
  }
  private setGroupName(groupName: string) {
    this.group = groupName;
    for (const edge of this.edges) {
      edge.group = groupName;
    }
  }
}
